import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { Case } from "../types";

// Shared normaliser: keeps the input constrained to what the backend
// will accept (lowercase, a-z 0-9, hyphens, max 40) so the user never
// sees surprise server-side rejections. 40 matches the server's
// validateSlug cap as of migration 0038.
function normaliseSlugClient(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 40);
}

interface Props {
  onCreated: (c: Case) => void;
  onCancel: () => void;
  // Distinct folder names across the user's cases. When empty the
  // form hides the folder field entirely — no folders exist yet, so
  // there's nothing meaningful to select. When non-empty the form
  // renders a dropdown rather than the legacy free-text autocomplete.
  folderNames?: string[];
  // Default folder selection. null = "Ungrouped". A string matching
  // a folderNames entry pre-selects that folder; anything else is
  // ignored (the form falls back to Ungrouped).
  initialFolder?: string | null;
  // When true, the folder dropdown is disabled. Used by the folder
  // row's hover "+" action, where the folder context is already
  // declared by the click site itself.
  folderLocked?: boolean;
}

interface Template {
  id: string;
  title: string;
  emoji: string;
  hint: string;
  description: string;
}

const TEMPLATES: Template[] = [
  {
    id: "blank",
    title: "Blank case",
    emoji: "📄",
    hint: "Start from scratch — no pre-filled description.",
    description: "",
  },
  {
    id: "bank",
    title: "Bank dispute",
    emoji: "🏦",
    hint: "Chargebacks, fraud, account errors, unauthorised transactions.",
    description: `Bank dispute — purpose: recover funds or reverse an erroneous charge.

Gather:
- Statements showing the disputed transaction(s)
- Any bank correspondence (chat logs, portal messages, call notes)
- Dates you reported the dispute, case reference numbers
- The bank's promises or timelines for investigation

Remember to scan any receipts for costs you have incurred as a result of this case — hotel stays, travel, replacement items, medical expenses. CaseThread will track your total costs automatically.

Typical timeline: initial dispute raised, 10-business-day provisional credit, up to 90 days for final determination. Escalate to ombudsman/FOS/CFPB if not resolved.`,
  },
  {
    id: "insurance",
    title: "Insurance claim",
    emoji: "🛡",
    hint: "Auto, home, health, disability — claim denied or underpaid.",
    description: `Insurance claim — purpose: obtain fair payout under policy terms.

Gather:
- Policy document and schedule of benefits
- Claim form + all submitted evidence
- Adjuster correspondence and inspection reports
- Denial or underpayment letters with the cited policy clause
- Any independent estimates or expert opinions

Remember to scan any receipts for costs you have incurred as a result of this case — hotel stays, travel, replacement items, medical expenses. CaseThread will track your total costs automatically.

Typical timeline: claim filed, 30-day acknowledgement, up to 90 days for decision. Escalate via internal review, state insurance commissioner, or small-claims court.`,
  },
  {
    id: "landlord",
    title: "Landlord / tenancy dispute",
    emoji: "🏠",
    hint: "Deposit withholding, repairs, eviction notices, lease disputes.",
    description: `Landlord / tenancy dispute — purpose: enforce tenancy rights.

Gather:
- Signed lease or tenancy agreement
- Inventory / check-in and check-out reports (with photos)
- Rent payment history
- Maintenance request history (dates, messages, photos)
- Deposit scheme protection certificate
- Notices served (Section 21/8 if UK, equivalent elsewhere)

Remember to scan any receipts for costs you have incurred as a result of this case — hotel stays, travel, replacement items, medical expenses. CaseThread will track your total costs automatically.

Typical timeline: informal request → formal letter before action → tribunal/court filing. Deposit disputes usually go via the protection scheme's free ADR.`,
  },
  {
    id: "employment",
    title: "Employment dispute",
    emoji: "💼",
    hint: "Wages owed, unfair dismissal, discrimination, harassment.",
    description: `Employment dispute — purpose: enforce employment rights or recover unpaid wages.

Gather:
- Employment contract and offer letter
- Pay slips and bank deposits
- Relevant emails, Slack/Teams messages, written warnings
- HR correspondence and any grievance filed
- Witness accounts (dated)
- Policy documents (handbook, code of conduct)

Remember to scan any receipts for costs you have incurred as a result of this case — hotel stays, travel, replacement items, medical expenses. CaseThread will track your total costs automatically.

Typical timeline: internal grievance → 28-day response → ACAS early conciliation (UK) or EEOC charge (US). Tribunal/lawsuit typically follows within months.`,
  },
  {
    id: "consumer",
    title: "Consumer complaint",
    emoji: "🛒",
    hint: "Faulty goods, services not delivered, refund refused.",
    description: `Consumer complaint — purpose: recover money or force performance.

Gather:
- Order confirmation, invoice, payment receipt
- Product photos or evidence of defect
- All correspondence with the seller (emails, chat, portal messages)
- Any terms & conditions referenced
- Reviews or evidence showing known issues

Remember to scan any receipts for costs you have incurred as a result of this case — hotel stays, travel, replacement items, medical expenses. CaseThread will track your total costs automatically.

Typical timeline: seller notified → 14-day response → chargeback with bank / Section 75 claim (UK) / Small claims court. Regulator escalation: Trading Standards (UK), FTC / state AG (US).`,
  },
  {
    id: "warranty",
    title: "Insurance or warranty claim",
    emoji: "🧾",
    hint: "Product defect, accident, or loss claim — itemise costs for recovery.",
    description: `Use this case to document a product defect, accident, or loss claim. Scan your receipts for repair costs, replacement items, medical expenses, and any other out-of-pocket costs. CaseThread will generate a formal Schedule of Loss for your claim.

Gather:
- Proof of purchase / warranty certificate / policy document
- Photos or evidence of the defect, damage, or loss
- Quotes or invoices for repair or replacement
- Medical bills, prescriptions, or therapy receipts (if applicable)
- Correspondence with the manufacturer, retailer, or insurer
- Any independent assessment or expert report

Remember to scan every receipt as you go — CaseThread tracks the running total and can generate a formal Schedule of Loss at any time from the Interrogate tab.

Typical timeline: claim filed → acknowledgement → adjuster review → decision (often 30–90 days). Escalate via internal review, ombudsman, or small claims court.`,
  },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function NewCaseForm({
  onCreated,
  onCancel,
  folderNames = [],
  initialFolder = null,
  folderLocked = false,
}: Props) {
  const [templateId, setTemplateId] = useState<string>("blank");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Seed the folder selection from the initial prop. Empty string
  // represents "Ungrouped" so a single <select> value can represent
  // both the no-folder case and real folder names.
  const [groupName, setGroupName] = useState<string>(() =>
    initialFolder && folderNames.includes(initialFolder) ? initialFolder : "",
  );
  const hasFolderDropdown = folderNames.length > 0;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touchedDescription, setTouchedDescription] = useState(false);
  const [descriptionFlash, setDescriptionFlash] = useState(false);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const [slug, setSlug] = useState("");
  const [slugCheck, setSlugCheck] = useState<{
    status: "idle" | "checking" | "ok" | "taken" | "invalid";
    message: string;
    fullAddress?: string;
  }>({ status: "idle", message: "" });
  const debounceRef = useRef<number | null>(null);

  // Live availability check, debounced so every keystroke doesn't fire a
  // request. 400ms feels responsive without hammering the endpoint.
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const trimmed = slug.trim();
    if (!trimmed) {
      setSlugCheck({ status: "idle", message: "" });
      return;
    }
    if (trimmed.length < 2) {
      setSlugCheck({
        status: "invalid",
        message: "Must be at least 2 characters",
      });
      return;
    }
    setSlugCheck({ status: "checking", message: "Checking…" });
    debounceRef.current = window.setTimeout(async () => {
      try {
        // checkEmailSlug covers both the new email_slug column and
        // the alias table (which includes every legacy
        // forward_address local-part as of migration 0038). Using
        // the stricter check prevents the create from failing at
        // INSERT time with a surprising "slug_collision" error.
        const r = await api.checkEmailSlug(trimmed);
        if (r.available) {
          setSlugCheck({
            status: "ok",
            message: `✓ ${r.full_address} is available`,
            fullAddress: r.full_address,
          });
        } else {
          // Suggest a variant to nudge the user toward a likely-free one.
          const suggestion = `${r.normalised}-${Math.random()
            .toString(36)
            .slice(2, 5)}`;
          setSlugCheck({
            status: r.error === "slug_reserved" ? "invalid" : "taken",
            message:
              r.error === "slug_reserved"
                ? "✗ This slug is reserved — pick another"
                : `✗ Already taken — try ${suggestion}`,
          });
        }
      } catch {
        setSlugCheck({ status: "idle", message: "" });
      }
    }, 400);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [slug]);

  function pickTemplate(t: Template) {
    setTemplateId(t.id);
    // Don't stomp on a description the user has already edited.
    if (!touchedDescription) setDescription(t.description);
    descriptionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    setDescriptionFlash(true);
    flashTimerRef.current = window.setTimeout(
      () => setDescriptionFlash(false),
      1000,
    );
  }

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { case: created } = await api.createCase(
        name.trim(),
        description.trim(),
        slug.trim() || undefined,
        groupName.trim() || null,
      );
      onCreated(created);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <form className="case-form" onSubmit={handleSubmit}>
      <h1>New case</h1>
      <p className="muted">
        A unique forwarding email address will be generated. Forward
        correspondence to that address and it will be filed here
        automatically.
      </p>

      {/* Folder field comes first when folders exist — it sets the
          context that the rest of the form operates in. When no
          folders exist at all, hide the field entirely (spec: "don't
          show the dropdown — behave as it does now") and let users
          create folders from the sidebar. */}
      {hasFolderDropdown && (
        <label>
          Folder
          <select
            className="new-case-folder-select"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            disabled={folderLocked}
          >
            <option value="">Ungrouped</option>
            {folderNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          {folderLocked && (
            <small className="muted">
              This case will be filed in <strong>{groupName}</strong>. Cancel
              and use <em>+ New case</em> at the top of the sidebar to pick a
              different folder.
            </small>
          )}
        </label>
      )}

      <div>
        <div className="case-form-label">Start from a template</div>
        <div className="template-grid">
          {TEMPLATES.map((t) => (
            <button
              type="button"
              key={t.id}
              className={`template-card${templateId === t.id ? " active" : ""}`}
              onClick={() => pickTemplate(t)}
              title={t.hint}
            >
              <span className="template-emoji" aria-hidden>
                {t.emoji}
              </span>
              <span className="template-title">{t.title}</span>
              <span className="template-hint">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <label>
        Name
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Ltd vs. Smith"
          maxLength={200}
          autoFocus
          required
        />
      </label>

      <label>
        Forwarding address (optional)
        <div className="slug-input-row">
          <input
            type="text"
            className="slug-input"
            value={slug}
            onChange={(e) => setSlug(normaliseSlugClient(e.target.value))}
            placeholder="acme-claim"
            maxLength={40}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="slug-suffix">@casethread.app</span>
        </div>
        <small
          className={`slug-status slug-status-${slugCheck.status}`}
          aria-live="polite"
        >
          {slugCheck.message ||
            "Leave blank to generate one automatically. Lowercase letters, numbers, and hyphens only."}
        </small>
      </label>

      <label>
        Description (optional)
        <textarea
          ref={descriptionRef}
          className={descriptionFlash ? "description-flash" : undefined}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setTouchedDescription(true);
          }}
          placeholder="What is this case about? Templates pre-fill this field — edit as needed."
          maxLength={4000}
          rows={10}
        />
      </label>
      {error && <div className="form-error">{error}</div>}
      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || !name.trim()}
        >
          {submitting ? "Creating…" : `Create case (${todayIso()})`}
        </button>
      </div>
    </form>
  );
}
